<template>
	<div class="p-p-2 p-ml-2 p-component" style="height: 100%;">
		<TabView v-model:activeIndex="activeTabOption" lazy>
			<TabPanel v-for="tab in tabOptions" :key="tab.value">
				<template #header>
					{{ tab.value }}
				</template>

				<div class="scroll-content">
					<FormTrainingItemIndicator
						v-if="tab.id === 0"
						:trainingKeyField="trainingKeyField"
						:documentClassId="documentClassId"
						:tables="tables"
						:indicatorOfFormTraining="indicatorOfFormTraining"
						:booleanOptions="booleanOptions"
						@testIndicator="testingData"
						@onFocusField="onFocusField"
						@onMarkRegion="onMarkRegion"
					/>
					<FormTrainingRegionAnchorItem
						v-else-if="tab.id === 1"
						:trainingKeyField="trainingKeyField"
						:documentClassId="documentClassId"
						:regionAnchorOfFormTraining="regionAnchorOfFormTraining"
						:booleanOptions="booleanOptions"
						@testRegionAnchor="testingData"
						@onFocusField="onFocusField"
						@onMarkRegion="onMarkRegion"
					/>
					<FormTrainingItemFields
						v-else-if="tab.id === 2"
						:trainingKeyField="trainingKeyField"
						:documentClassId="documentClassId"
						:tables="tables"
						:itemFieldOfRegionInFormTraining="itemFieldOfRegionInFormTraining"
						:booleanOptions="booleanOptions"
						@testItemField="testingData"
						@onFocusField="onFocusField"
						@onMarkRegion="onMarkRegion"
					/>
					<FormTrainingTestTable
						v-else-if="tab.id === 3"
						:trainingKeyField="trainingKeyField"
						:documentClassId="documentClassId"
						:documentId="documentId"
						:testFilter="testFilter"
						@onMarkRegion="onMarkRegion"
					/>
				</div>
			</TabPanel>
		</TabView>
	</div>
</template>

<script lang="ts">
import {computed, defineComponent, PropType, ref} from "vue";
import {useI18n} from "vue-i18n";
import {useToast} from "primevue/usetoast";
import {
	DocumentField,
	DocumentTable, FormTrainingItemField,
	FormTrainingItemRegionAnchor,
	FormTrainingRegionAnchor
} from "@dex/squeeze-client-ts";
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';
import FormTrainingItemIndicator from "@/apps/squeeze/components/FormTrainingItemIndicator.vue";
import FormTrainingRegionAnchorItem from "@/apps/squeeze/components/FormTrainingRegionAnchorItem.vue";
import FormTrainingItemFields from "@/apps/squeeze/components/FormTrainingItemFields.vue";
import FormTrainingTestTable from "@/apps/squeeze/components/FormTrainingTestTable.vue";

export default defineComponent({
	name: "FormPositionTraining",
	components: {
		FormTrainingTestTable,
		FormTrainingItemFields,
		FormTrainingRegionAnchorItem,
		FormTrainingItemIndicator,
		TabView,
		TabPanel,
	},
	props: {
		documentId: {
			type: Number,
			required: true,
		},
		documentClassId: {
			type: Number,
			required: true,
		},
		tables: {
			type: Array as PropType<DocumentTable[]>,
			default: () => [],
		},
		trainingKeyField: {
			type: Object as PropType<DocumentField>,
			default: () => ({}),
		},
		indicatorOfFormTraining: {
			type: Object as PropType<FormTrainingItemRegionAnchor>,
			default: () => ({}),
		},
		regionAnchorOfFormTraining: {
			type: Object as PropType<FormTrainingRegionAnchor>,
			default: () => ({}),
		},
		itemFieldOfRegionInFormTraining: {
			type: Object as PropType<FormTrainingItemField>,
			default: () => ({}),
		},
	},
	emits: [
		'onFocusField',
		'onMarkRegion',
	],
	setup(props, {emit}) {
		const {t} = useI18n();
		const toast = useToast();

		/** Current active tab option */
		const activeTabOption = ref(0);

		/** All tab options of a file */
		const tabOptions = ref([
			{id: 0, value: t('Squeeze.Training.Indicator')},
			{id: 1, value: t('Squeeze.Training.Region') + ' ' + t('Squeeze.Training.Anchor')},
			{id: 2, value: t('Squeeze.Training.Region') + ' ' + t('Squeeze.Training.Fields')},
			{id: 3, value: t('Squeeze.Training.Test')},
		]);

		/** List of boolean options */
		const booleanOptions = computed(() => {
			return [
				{
					id: true,
					name: t('Squeeze.General.Yes'),
				},
				{
					id: false,
					name: t('Squeeze.General.No'),
				},
			]
		});

		/**  Test filter */
		const testFilter = ref<any>({});

		/**
		 * Test data
		 * @param rowData
		 */
		const testingData = (rowData: any) => {
			activeTabOption.value = 3;
			testFilter.value = rowData;
		}

		/**
		 * Triggered when a field is focused
		 * @param {string} fieldName
		 */
		const onFocusField = (fieldName: string) => {
			emit("onFocusField", fieldName);
		}

		/**
		 * Set marker regions by clicking a row
		 * @param region
		 */
		const onMarkRegion = (region: any) => {
			emit("onMarkRegion", region);
		}

		return {
			t,
			toast,
			activeTabOption,
			tabOptions,
			booleanOptions,
			testFilter,
			testingData,
			onFocusField,
			onMarkRegion,
		}
	},
});
</script>

<style scoped>

::v-deep(.p-tabview) {
	height: calc(100vh - 10rem);
}

::v-deep(.p-tabview-panels), ::v-deep(.p-tabview-panel) {
	height: 100%;
}

.scroll-content {
	height: calc(100vh - 31.15rem);
}

</style>
